import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { degradedCopyKeys, type DegradedContext } from '@/lib/limulo/gating';
import type { LimuloFailure } from '@/lib/limulo/errors';

/**
 * IL DEGRADO ONESTO — la cosa che questa sezione consegna davvero, oggi.
 *
 * Il vincolo di ADR-0078 e' esplicito: ogni schermata degrada su 424/403 mostrando «non
 * ancora servito da Limulo», MAI un finto vuoto. La ragione e' che un elenco vuoto e una
 * capability senza provider si assomigliano moltissimo a schermo e non si assomigliano
 * per niente nella realta': il primo dice «non ci sono tornei», il secondo dice «nessuno
 * risponde a questa domanda». Confonderli manda una persona a cercare i tornei che non
 * ci sono, e un'altra a cercare un guasto che non esiste.
 *
 * Una famiglia di frasi per verdetto (v. `gating.ts`) — e per il 424, che collassa due
 * cause diverse, una frase SOLA, corta, che cambia col contesto e non promette un
 * ritorno. E il `correlationId` in fondo quando c'e': e' l'unica cosa che permette a chi tiene il BFF di ritrovare
 * ESATTAMENTE questa richiesta nei suoi log, invece di cercare «un 424 di stamattina».
 */
export function LimuloDegraded({
  failure,
  context,
  back,
}: {
  failure: LimuloFailure;
  /** Cosa stava guardando l'utente: cambia solo la frase del 424 (v. `gating.ts`). */
  context?: DegradedContext;
  /**
   * **Una via d'uscita, e la decide CHI CHIAMA.**
   *
   * 🔴 Perche' serve: le rotte `/limulo/*` **non sono rotte-scheda** ⇒ ~~`showsTabBar` e' falso
   * (`AppShell.tsx:83-85`) ⇒ **la TabBar non c'e' su nessuna di esse**~~ — dal 2026-09-27 la TabBar
   * c'e' nella sezione tornei (Luca; `components/layout/tabBarRoutes.ts`), ma porta alle cinque
   * schede, non al posto giusto (gli incontri, il torneo): questa uscita resta. Fino ad allora
   * l'unica uscita residua era il tasto indietro di Telegram — che `useBackButton` implementa con
   * `navigate(-1)` **senza nessun ripiego** (`:16,25`): su un arrivo per link diretto **non
   * porta da nessuna parte**. ⇒ Dove questo pannello **e'** la schermata (early return),
   * senza questa uscita l'utente era in un vicolo cieco (dal 27/09 non piu': la TabBar lo porta
   * alle cinque schede, ma non al torneo da cui e' entrato). Misurati: **sette** su tredici usi
   * (`reports/2026-09-10-passo2-gruppo-a-disegno.md` §D).
   *
   * ⚠️ **Opzionale, e l'opzionalita' e' il punto**: dove non si passa, **niente cambia** — i
   * cinque usi che sono un blocco dentro una pagina viva hanno gia' le loro strade, e
   * aggiungerne un'altra sarebbe rumore.
   *
   * ⛔ **La destinazione la conosce il chiamante, non il pannello.** Un ritorno deciso qui
   * sarebbe lo stesso identico link in sette posti diversi, e in almeno quattro sarebbe
   * sbagliato: da un incontro si torna agli incontri, non alla lista dei tornei.
   * ⇒ ⛔ **E dove una destinazione vera non c'e', non si passa niente**: meglio nessuna
   * strada che una inventata (stessa regola dello slot `via` di `PanelNotice`).
   */
  back?: { to: string; label: string };
}) {
  const { t } = useTranslation('limulo');
  const { titleKey, bodyKey } = degradedCopyKeys(failure.verdict, context);

  return (
    <div data-testid="stato-degrado" className="rounded-xl border border-border bg-bg-secondary p-4">
      <h2 className="text-base font-semibold text-fg">{t(titleKey)}</h2>
      {/* Il corpo non c'e' sempre: il 424 e' una frase e basta, corta e senza promesse
          (decisione Luca 2026-08-31). Aggiungere qui una spiegazione «per completezza»
          significherebbe rimettere addosso all'utente un dubbio che e' nostro. */}
      {bodyKey ? <p className="mt-1 text-sm text-fg-muted">{t(bodyKey)}</p> : null}

      {/* Il messaggio del server si mostra COSI' COM'E', non tradotto e non riassunto:
          e' l'unico pezzo di verita' che arriva dall'altra parte, e riscriverlo con
          parole nostre e' il modo piu' rapido per far sparire l'informazione utile. */}
      {failure.message ? (
        <p className="mt-3 font-mono text-xs break-words text-fg-muted">{failure.message}</p>
      ) : null}

      {failure.correlationId ? (
        <p className="mt-2 font-mono text-xs text-fg-muted">
          {t('degraded.correlation', { id: failure.correlationId })}
        </p>
      ) : null}

      {back ? (
        <Link
          data-tour="limulo-degraded-back"
          to={back.to}
          className="mt-4 inline-block text-sm font-semibold text-brand"
        >
          {back.label}
        </Link>
      ) : null}
    </div>
  );
}
